import React, { useState } from 'react';
import { Plus, Edit2, Trash2, Check, Package } from 'lucide-react';
interface Distributor {
  id: string;
  name: string;
  rings: number;
  stripePublishableKey: string;
  stripeSecretKey: string;
}
interface DistributorsManagerProps {
  distributors: Distributor[];
  onAdd: (name: string, rings: number, publishableKey: string, secretKey: string) => Promise<void>;
  onEdit: (index: number, name: string, rings: number, publishableKey: string, secretKey: string) => Promise<void>;
  onDelete: (index: number) => Promise<void>;
  getDistributorColor: (name: string) => string;
}
export function DistributorsManager({
  distributors,
  onAdd,
  onEdit,
  onDelete,
  getDistributorColor
}: DistributorsManagerProps) {
  const [newDistributorName, setNewDistributorName] = useState('');
  const [newDistributorRings, setNewDistributorRings] = useState(0);
  const [newDistributorStripePublishable, setNewDistributorStripePublishable] = useState('');
  const [newDistributorStripeSecret, setNewDistributorStripeSecret] = useState('');
  const [editingDistributorIndex, setEditingDistributorIndex] = useState<number | null>(null);
  const [editingDistributorName, setEditingDistributorName] = useState('');
  const [editingDistributorRings, setEditingDistributorRings] = useState(0);
  const [editingDistributorStripePublishable, setEditingDistributorStripePublishable] = useState('');
  const [editingDistributorStripeSecret, setEditingDistributorStripeSecret] = useState('');
  const handleAdd = async () => {
    if (newDistributorName.trim()) {
      await onAdd(newDistributorName.trim(), newDistributorRings, newDistributorStripePublishable.trim(), newDistributorStripeSecret.trim());
      setNewDistributorName('');
      setNewDistributorRings(0);
      setNewDistributorStripePublishable('');
      setNewDistributorStripeSecret('');
    }
  };
  const startEditing = (index: number) => {
    setEditingDistributorIndex(index);
    setEditingDistributorName(distributors[index].name);
    setEditingDistributorRings(distributors[index].rings);
    setEditingDistributorStripePublishable(distributors[index].stripePublishableKey || '');
    setEditingDistributorStripeSecret(distributors[index].stripeSecretKey || '');
  };
  const saveEdit = async () => {
    if (editingDistributorIndex !== null && editingDistributorName.trim()) {
      await onEdit(editingDistributorIndex, editingDistributorName.trim(), editingDistributorRings, editingDistributorStripePublishable.trim(), editingDistributorStripeSecret.trim());
      setEditingDistributorIndex(null);
      setEditingDistributorName('');
      setEditingDistributorRings(0);
      setEditingDistributorStripePublishable('');
      setEditingDistributorStripeSecret('');
    }
  };
  const cancelEdit = () => {
    setEditingDistributorIndex(null);
    setEditingDistributorName('');
    setEditingDistributorRings(0);
    setEditingDistributorStripePublishable('');
    setEditingDistributorStripeSecret('');
  };
  return <div className="p-6" data-id="element-586">
      <div className="mb-6" data-id="element-587">
        <h2 className="text-xl font-semibold text-slate-800 mb-2" data-id="element-588">
          Manage Distributors
        </h2>
        <p className="text-sm text-slate-600" data-id="element-589">
          Add, edit, or remove distributors and assign ring counts
        </p>
      </div>

      {/* Add New Distributor */}
      <div className="bg-slate-50 rounded-lg p-4 mb-6" data-id="element-590">
        <h3 className="text-sm font-medium text-slate-700 mb-3" data-id="element-591">
          Add New Distributor
        </h3>
        <div className="space-y-3" data-id="element-592">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-593">
            <input type="text" value={newDistributorName} onChange={e => setNewDistributorName(e.target.value)} onKeyPress={e => e.key === 'Enter' && handleAdd()} placeholder="Enter distributor name..." className="px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-594" />
            <input type="number" value={newDistributorRings} onChange={e => setNewDistributorRings(parseInt(e.target.value) || 0)} onKeyPress={e => e.key === 'Enter' && handleAdd()} placeholder="Rings" min="0" className="w-24 px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-595" />
          </div>
          <div className="space-y-2" data-id="element-596">
            <input type="text" value={newDistributorStripePublishable} onChange={e => setNewDistributorStripePublishable(e.target.value)} placeholder="Stripe Publishable Key (pk_...)" className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm" data-id="element-597" />
            <input type="password" value={newDistributorStripeSecret} onChange={e => setNewDistributorStripeSecret(e.target.value)} placeholder="Stripe Secret Key (sk_...)" className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm" data-id="element-598" />
          </div>
          <button onClick={handleAdd} disabled={!newDistributorName.trim()} className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-slate-300 disabled:cursor-not-allowed" data-id="element-599">
            <Plus size={18} data-id="element-600" />
            Add Distributor
          </button>
        </div>
      </div>

      {/* Distributor List */}
      <div className="space-y-2" data-id="element-601">
        <h3 className="text-sm font-medium text-slate-700 mb-3" data-id="element-602">
          Current Distributors ({distributors.length})
        </h3>
        {distributors.map((dist, index) => <div key={index} className="flex items-start justify-between p-4 border border-slate-200 rounded-lg hover:border-slate-300 transition-colors" data-id="element-603">
            {editingDistributorIndex === index ? <div className="flex-1 space-y-3" data-id="element-604">
                <div className="flex items-center gap-3" data-id="element-605">
                  <input type="text" value={editingDistributorName} onChange={e => setEditingDistributorName(e.target.value)} onKeyPress={e => e.key === 'Enter' && saveEdit()} className="flex-1 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" autoFocus data-id="element-606" />
                  <input type="number" value={editingDistributorRings} onChange={e => setEditingDistributorRings(parseInt(e.target.value) || 0)} onKeyPress={e => e.key === 'Enter' && saveEdit()} min="0" className="w-24 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-607" />
                </div>
                <div className="space-y-2" data-id="element-608">
                  <input type="text" value={editingDistributorStripePublishable} onChange={e => setEditingDistributorStripePublishable(e.target.value)} placeholder="Stripe Publishable Key (pk_...)" className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm" data-id="element-609" />
                  <input type="password" value={editingDistributorStripeSecret} onChange={e => setEditingDistributorStripeSecret(e.target.value)} placeholder="Stripe Secret Key (sk_...)" className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm" data-id="element-610" />
                </div>
                <div className="flex items-center gap-2" data-id="element-611">
                  <button onClick={saveEdit} className="px-4 py-2 text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors flex items-center gap-2" data-id="element-612">
                    <Check size={18} data-id="element-613" />
                    Save
                  </button>
                  <button onClick={cancelEdit} className="px-4 py-2 text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors" data-id="element-614">
                    Cancel
                  </button>
                </div>
              </div> : <>
                <div className="flex-1" data-id="element-615">
                  <div className="flex items-center gap-3 mb-2" data-id="element-616">
                    <span className="text-slate-800 font-medium text-lg" data-id="element-617">
                      {dist.name}
                    </span>
                    <span className={`text-xs px-2 py-1 rounded border ${getDistributorColor(dist.name)}`} data-id="element-618">
                      {dist.rings} {dist.rings === 1 ? 'Ring' : 'Rings'}
                    </span>
                  </div>
                  {(dist.stripePublishableKey || dist.stripeSecretKey) && <div className="space-y-1 text-xs text-slate-600" data-id="element-619">
                      {dist.stripePublishableKey && <div className="flex items-center gap-2" data-id="element-620">
                          <span className="font-medium" data-id="element-621">Publishable:</span>
                          <span className="font-mono bg-slate-100 px-2 py-0.5 rounded" data-id="element-622">
                            {dist.stripePublishableKey.substring(0, 20)}...
                          </span>
                        </div>}
                      {dist.stripeSecretKey && <div className="flex items-center gap-2" data-id="element-623">
                          <span className="font-medium" data-id="element-624">Secret:</span>
                          <span className="font-mono bg-slate-100 px-2 py-0.5 rounded" data-id="element-625">
                            ••••••••••••••••
                          </span>
                        </div>}
                    </div>}
                </div>
                <div className="flex items-center gap-2" data-id="element-626">
                  <button onClick={() => startEditing(index)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" title="Edit" data-id="element-627">
                    <Edit2 size={16} data-id="element-628" />
                  </button>
                  <button onClick={() => onDelete(index)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="Delete" data-id="element-629">
                    <Trash2 size={16} data-id="element-630" />
                  </button>
                </div>
              </>}
          </div>)}
      </div>

      {distributors.length === 0 && <div className="text-center py-12" data-id="element-631">
          <Package size={48} className="text-slate-300 mx-auto mb-4" data-id="element-632" />
          <p className="text-slate-600" data-id="element-633">No distributors added yet</p>
          <p className="text-sm text-slate-500 mt-1" data-id="element-634">
            Add your first distributor above
          </p>
        </div>}
    </div>;
}